import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail as Mail, FiArrowLeft as ArrowLeft } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { requestPasswordReset } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) { setError('Email is required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Enter a valid email address.'); return; }

    setIsLoading(true);
    const result = await requestPasswordReset(email);
    setIsLoading(false);

    if (result.success) {
      setSubmitted(true);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center space-x-3 group mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#DC143C] to-[#B01030] rounded-lg flex items-center justify-center group-hover:shadow-lg transition-all duration-200">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <div className="text-left">
              <div className="text-lg font-bold text-gray-900">Pinoy General Insurance</div>
              <div className="text-xs text-gray-600">Services</div>
            </div>
          </Link>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Forgot Your Password?</h2>
          <p className="text-gray-600">
            {submitted
              ? "Check your inbox for a reset link."
              : "Enter your email and we'll send you a reset link."}
          </p>
        </div>

        <div className="bg-white py-8 px-8 shadow-xl rounded-xl border border-gray-200">
          {submitted ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                  <Mail size={28} className="text-green-600" />
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                If an account exists for{' '}
                <span className="font-semibold text-gray-900">{email}</span>,
                you'll receive a password reset link shortly. Check your spam folder if you don't see it.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-[#0038A8] hover:underline font-medium mt-2"
              >
                <ArrowLeft size={16} /> Back to Sign In
              </Link>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit} noValidate>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent transition-all duration-200 ${error ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#DC143C] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#B01030] focus:ring-4 focus:ring-[#DC143C]/20 transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <><div className="spinner"></div> Sending Reset Link...</>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>
          )}
        </div>

        <div className="text-center">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft size={14} /> Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
